import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import "../css/Header.css";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "react-oidc-context";
import { User, LogOut } from "lucide-react";
import config from '../config';
import { Link, useNavigate } from "react-router-dom";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && !event.target.closest('.menu-icon')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('.account-menu')) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  if (auth.isLoading) {
    return <div className="header-loading">Loading...</div>;
  }

  if (auth.error) {
    return <div className="header-error">Encountering error... {auth.error.message}</div>;
  }

  const signOutRedirect = () => {
    const clientId = config.clientId;
    const logoutUri = config.logoutUri;
    const cognitoDomain = config.cognitoDomain;
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    auth.removeUser();
  };

  const handleMobileLinkClick = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleAccountClick = () => {
    setIsOpen(false);
    if (auth.isAuthenticated) {
      navigate('/account');
    } else {
      auth.signinRedirect();
    }
  };

  return (
    <header className="header-wrapper">
      <nav className="navbar">
        <div className="navbar-content">
          <Link to="/" className="logo-container" onClick={() => setIsOpen(false)}>
            <img src="/logo1.PNG" alt="SmartKids Prep" className="logo-img" />
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            <Link to="/about" className="nav-link">About</Link>
            <Link to="/contact" className="nav-link">Contact</Link>
          </div>

          {/* Desktop Account Section */}
          <div className="account-section desktop-account">
            {auth.isAuthenticated ? (
              <div className="account-menu">
                <button 
                  className="avatar-button" 
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label="Account menu"
                >
                  <img src="/account.PNG" alt="User Avatar" className="avatar-image" />
                </button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="dropdown-menu"
                    >
                      <button 
                        className="menu-item" 
                        onClick={() => {
                          setMenuOpen(false);
                          navigate('/account');
                        }}
                      >
                        <User size={18} /> Account Details
                      </button>
                      <button 
                        className="menu-item logout" 
                        onClick={() => {
                          setMenuOpen(false);
                          signOutRedirect();
                        }}
                      >
                        <LogOut size={18} /> Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button 
                className="cta-button" 
                onClick={() => auth.signinRedirect()}
              >
                My Account
              </button>
            )}
          </div>

          {/* Mobile Menu Icon */}
          <button 
            className="menu-icon" 
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="icon" size={24} /> : <Menu className="icon" size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mobile-menu-overlay"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              ref={menuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="mobile-menu"
            >
              <div className="mobile-menu-header">
                <img src="/logo1.PNG" alt="SmartKids Prep" className="mobile-logo" />
                <button 
                  className="mobile-menu-close" 
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="mobile-menu-content">
                <Link 
                  to="/about" 
                  className="mobile-link" 
                  onClick={() => handleMobileLinkClick('/about')}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="mobile-link" 
                  onClick={() => handleMobileLinkClick('/contact')}
                >
                  Contact
                </Link>
                
                <div className="mobile-menu-divider" />
                
                {auth.isAuthenticated ? (
                  <>
                    <button 
                      className="mobile-link mobile-account-button" 
                      onClick={handleAccountClick}
                    >
                      <User size={20} />
                      <span>My Account</span>
                    </button>
                    <button 
                      className="mobile-link mobile-logout-button" 
                      onClick={() => {
                        setIsOpen(false);
                        signOutRedirect();
                      }}
                    >
                      <LogOut size={20} />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <button 
                    className="mobile-link mobile-login-button" 
                    onClick={() => {
                      setIsOpen(false);
                      auth.signinRedirect();
                    }}
                  >
                    <User size={20} />
                    <span>Sign In / My Account</span>
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;





